import { observer } from 'mobx-react-lite';
import { Navigate } from 'react-router-dom';

import { useDependencies } from '../dependencies';
import { ExternalRedirect } from './ExternalRedirect';

// Resolves an unscoped entry-point URL (e.g. /app/saved-searches) to the
// current user's scoped URL (e.g. /app/parent/:id/saved-searches). Used for
// marketing emails, push notifications, and bookmarks where the link is built
// without knowing the recipient's user id.
interface Props {
  pathTemplate: string; // e.g. 'parent/:id/saved-searches'
  fallbackRole?: 'parent' | 'sitter';
}

export const UserScopedRedirect = observer(
  ({ pathTemplate, fallbackRole = 'parent' }: Props): JSX.Element => {
    const { stores } = useDependencies();
    const user = stores.user.user;
    if (!user?.id) {
      // /login lives outside the BrowserRouter basename='/app', so a plain
      // <Navigate> would resolve to /app/login. ExternalRedirect uses
      // window.location.replace to escape the basename. window.location.pathname
      // gives the full /app/... path so it round-trips after login.
      const intended = `${window.location.pathname}${window.location.search}`;
      return (
        <ExternalRedirect
          to={`/login?redirectTo=${encodeURIComponent(intended)}`}
        />
      );
    }
    const role = user.parent
      ? 'parent'
      : user.sitter
        ? 'sitter'
        : fallbackRole;
    const target = `/${pathTemplate
      .replace(':role', role)
      .replace(':id', String(user.id))}`;
    return <Navigate to={target} replace />;
  },
);
